import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '../Components/Grid/Grid';
import Card from '../Components/Card/Card';
import Tabs from '../Components/Tabs/Tabs';
import { useTabs } from '../Hooks/Tabs/useTabs';
import commonActions from '../Actions/commonActions';
import feedbackActions from '../Actions/feedbackActions';
import FeedbackForm from '../Components/Feedback/FeedbackForm';
import FeedbackList from '../Components/Feedback/FeedbackList';
import MyFeedbackList from '../Components/Feedback/MyFeedbackList';
import UpvoteBudgetChip from '../Components/Feedback/UpvoteBudgetChip';
import useUserAccess from '../Hooks/useUserAccess';
import { UserRoles } from '../Constants/userRoles';
import '../Components/Feedback/Feedback.css';

const FeedbackContainer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const suggestions = useSelector((state) => state.FeedbackReducer.suggestions);
    const mySuggestions = useSelector((state) => state.FeedbackReducer.mySuggestions);
    const remainingUpvotes = useSelector((state) => state.FeedbackReducer.remainingUpvotes);
    const maxUpvotes = useSelector((state) => state.FeedbackReducer.maxUpvotes);
    const maxSuggestionLength = useSelector((state) => state.FeedbackReducer.maxSuggestionLength);
    const pendingUpvotes = useSelector((state) => state.FeedbackReducer.pendingUpvotes);
    const feedbackListLoading = useSelector((state) => state.CommonReducer.dataLoading.feedbackList);
    const myFeedbackLoading = useSelector((state) => state.CommonReducer.dataLoading.myFeedback);

    const { hasReadWriteAccess } = useUserAccess();
    const canWrite = hasReadWriteAccess(UserRoles.Spcs);

    const tabs = useMemo(
        () => [
            { id: 'public', label: t('feedback.tabAllPublic') },
            { id: 'mine', label: t('feedback.tabMine') },
        ],
        [t]
    );
    const { activeTab, setActiveTab } = useTabs(tabs[0]);
    const isMineTab = activeTab?.id === 'mine';

    const loadLists = () => {
        dispatch(feedbackActions.getFeedbackList());
        dispatch(feedbackActions.getMyFeedback());
    };

    useEffect(() => {
        loadLists();
    }, [dispatch]);

    const handleSubmit = (suggestion, isPublic) =>
        dispatch(feedbackActions.submitFeedback(suggestion, isPublic)).then(() => {
            dispatch(commonActions.showSuccess(t('feedback.submitted')));
            loadLists();
        });

    const handleUpvote = (suggestionId) => dispatch(feedbackActions.toggleUpvote(suggestionId));

    return (
        <div className="col-xs-12">
            <Grid>
                <Grid.Row>
                    <Grid.Row.Column width={4}>
                        <Card>
                            <Card.Header className="feedback-card-heading">
                                <Card.Header.Title>{t('feedback.title')}</Card.Header.Title>
                            </Card.Header>
                            <Card.Content>
                                <p className="text-secondary mb-16">{t('feedback.intro')}</p>
                                {canWrite && <FeedbackForm maxSuggestionLength={maxSuggestionLength} onSubmit={handleSubmit} />}
                            </Card.Content>
                        </Card>
                    </Grid.Row.Column>

                    <Grid.Row.Column>
                        <Card>
                            <div className="feedback-tabs-row d-flex align-items-center justify-content-between gap-12 pr-24 border-bottom">
                                <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                                {!isMineTab && canWrite && remainingUpvotes != null && <UpvoteBudgetChip remaining={remainingUpvotes} total={maxUpvotes} />}
                            </div>
                            <Card.Content isLoading={isMineTab ? myFeedbackLoading : feedbackListLoading}>
                                {isMineTab ? (
                                    <MyFeedbackList suggestions={mySuggestions} />
                                ) : (
                                    <FeedbackList
                                        suggestions={suggestions}
                                        remainingUpvotes={remainingUpvotes}
                                        maxUpvotes={maxUpvotes}
                                        canWrite={canWrite}
                                        pendingUpvotes={pendingUpvotes}
                                        onUpvote={handleUpvote}
                                    />
                                )}
                            </Card.Content>
                        </Card>
                    </Grid.Row.Column>
                </Grid.Row>
            </Grid>
        </div>
    );
};

export default FeedbackContainer;
